import { useState } from "react";
import { modules } from "@/data/roadmap";
import { ROADMAP } from "@/constants/testIds/roadmap";
import { SectionLabel } from "./ExecSummary";
import * as Icons from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { motion } from "framer-motion";

export default function Modules() {
  const [open, setOpen] = useState(null);
  const activeModule = modules.find((m) => m.id === open);

  return (
    <section
      id="modules"
      data-testid={ROADMAP.modulesSection}
      className="py-20 lg:py-28 border-b border-[#DEE2E6] bg-[#F8F9FA]"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <SectionLabel n="03" label="ERP Modules" />
        <div className="grid lg:grid-cols-12 gap-6 mt-3 items-end">
          <h2 className="lg:col-span-7 font-heading text-3xl md:text-4xl lg:text-5xl tracking-tighter font-bold">
            Eight modules. Every workflow your business runs on.
          </h2>
          <p className="lg:col-span-5 text-[#495057] leading-relaxed">
            Each module ships with workflows, internal controls, and configuration mapped directly
            from the Gigmann blueprint. Click any module to drill in.
          </p>
        </div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-0 border border-[#0A0A0A]">
          {modules.map((m, i) => {
            const Icon = Icons[m.icon] || Icons.Box;
            const isLastRow = i >= modules.length - (modules.length % 4 || 4);
            return (
              <motion.button
                key={m.id}
                onClick={() => setOpen(m.id)}
                data-testid={ROADMAP.moduleCard(m.id)}
                whileHover={{ y: -2 }}
                transition={{ duration: 0.2 }}
                className={`group text-left p-6 bg-white hover:bg-[#0A0A0A] hover:text-white transition-colors duration-200 border-r border-b border-[#0A0A0A] ${
                  (i + 1) % 4 === 0 ? "lg:border-r-0" : ""
                } ${(i + 1) % 2 === 0 ? "md:border-r-0 lg:border-r" : ""} ${
                  (i + 1) % 4 === 0 ? "lg:border-r-0" : ""
                }`}
                style={{
                  borderBottom: isLastRow ? "none" : undefined,
                }}
              >
                <div className="flex items-start justify-between mb-6">
                  <div className="w-10 h-10 border border-current flex items-center justify-center">
                    <Icon size={18} />
                  </div>
                  <span className="font-mono text-xs opacity-50 group-hover:opacity-100">
                    M0{i + 1}
                  </span>
                </div>
                <h3 className="font-heading text-lg font-semibold tracking-tight leading-tight">
                  {m.name}
                </h3>
                <div className="mt-4 text-xs uppercase tracking-[0.15em] opacity-60">
                  {m.workflows.length} workflows
                </div>
                <div className="mt-6 inline-flex items-center gap-1 text-xs uppercase tracking-[0.15em] font-semibold opacity-60 group-hover:opacity-100 group-hover:text-[#FF4F00]">
                  View detail →
                </div>
              </motion.button>
            );
          })}
        </div>
      </div>

      <Dialog open={!!open} onOpenChange={() => setOpen(null)}>
        <DialogContent
          data-testid={ROADMAP.moduleDialog}
          className="max-w-4xl max-h-[85vh] overflow-y-auto rounded-none border-2 border-[#0A0A0A] bg-white"
        >
          {activeModule && (
            <>
              <DialogHeader>
                <div className="text-xs uppercase tracking-[0.2em] text-[#FF4F00] font-mono mb-2">
                  Gr8Boost ERP · Module
                </div>
                <DialogTitle className="font-heading text-3xl tracking-tighter font-bold">
                  {activeModule.name}
                </DialogTitle>
                <DialogDescription className="text-[#495057]">
                  All workflows, internal controls, and configuration requirements for{" "}
                  {activeModule.name.toLowerCase()}.
                </DialogDescription>
              </DialogHeader>

              <div className="grid md:grid-cols-3 gap-6 mt-4">
                <Block title="Workflows">
                  <ol className="space-y-2 list-none">
                    {activeModule.workflows.map((w, idx) => (
                      <li key={w} className="grid grid-cols-[auto_1fr] gap-2 text-sm">
                        <span className="font-mono text-xs text-[#495057] tabular-nums">
                          {String(idx + 1).padStart(2, "0")}
                        </span>
                        <span className="text-[#0A0A0A]">{w}</span>
                      </li>
                    ))}
                  </ol>
                </Block>
                <Block title="Internal Controls">
                  <ul className="space-y-2">
                    {activeModule.controls.map((c) => (
                      <li key={c} className="text-sm text-[#0A0A0A] grid grid-cols-[auto_1fr] gap-2">
                        <span className="text-[#0B7A75] font-bold">✓</span>
                        <span>{c}</span>
                      </li>
                    ))}
                  </ul>
                </Block>
                <Block title="ERP Configuration">
                  <ul className="space-y-2">
                    {activeModule.configRequirements.map((c) => (
                      <li key={c} className="text-sm text-[#0A0A0A] grid grid-cols-[auto_1fr] gap-2">
                        <span className="text-[#0033A0] font-bold">▸</span>
                        <span>{c}</span>
                      </li>
                    ))}
                  </ul>
                </Block>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}

function Block({ title, children }) {
  return (
    <div className="border border-[#DEE2E6] bg-[#F8F9FA] p-5">
      <div className="text-[11px] uppercase tracking-[0.2em] font-semibold text-[#0A0A0A] mb-4">
        {title}
      </div>
      {children}
    </div>
  );
}
